import moment from 'moment';
import { IGroupFormData } from '../../components/group-management';
import firebase from '../../firebase-integration/index';
import { IGroupListConfig } from '../../store';
import { IGroupListRepsonse } from '../interfaces/groups';
import { searchTerms } from '../utils';

const db = firebase.firestore();
const groupsCollection = db.collection('groups');
const usersCollection = db.collection('users');

export const createGroup =
  async (user: any, group: IGroupFormData) => {
    if (!group) {
      throw new Error('No Group information has been provided');
    }

    try {
      const terms = searchTerms([group.name]);

      const newGroup = await groupsCollection
        .add({
          searchTerms: terms,
          name: group.name,
          type: group.type,
          ownerId: user.uid,
          archived: false,
          created: firebase.firestore.FieldValue.serverTimestamp(),
          updated: firebase.firestore.FieldValue.serverTimestamp(),
        });

      return await groupsCollection
        .doc(newGroup.id)
        .collection('members')
        .doc(user.email)
        .set({
          type: 'owner',
          id: user.uid,
          name: user.displayName,
          email: user.email,
          joinedDate: firebase.firestore.FieldValue.serverTimestamp(),
          approvedDate: firebase.firestore.FieldValue.serverTimestamp(),
          status: 'approved',
          created: firebase.firestore.FieldValue.serverTimestamp(),
          updated: firebase.firestore.FieldValue.serverTimestamp(),
        });
    } catch (error) {
      throw error;
    }
  };

export const deleteGroup =
  async (groupId: string): Promise<void> => {
    return await groupsCollection
      .doc(groupId)
      .update({
        archived: true,
        updated: firebase.firestore.FieldValue.serverTimestamp(),
      });
  };

export const updateGroup =
  async (group: IGroupFormData): Promise<void> => {
    const terms = searchTerms([group.name]);

    return await groupsCollection
      .doc(group.id)
      .update({
        searchTerms: terms,
        name: group.name,
        type: group.type,
        updated: firebase.firestore.FieldValue.serverTimestamp(),
      });
  };

export const groupNameExists =
  async (userId: string, groupName: string) => {
    console.log('values', userId, groupName);

    const documents = await groupsCollection
      .where('archived', '==', false)
      .where('ownerId', '==', userId)
      .where('name', '==', groupName)
      .get();

    let count = 0;
    documents.forEach((data: any) => {
      count = count + 1;
    });

    return count;
  };

export const getGroupsList =
  async (userId: string, config: IGroupListConfig): Promise<IGroupListRepsonse> => {
    if (!userId) {
      throw new Error('No User Id was provided');
    }

    const documentQuery = groupsCollection
      .where('archived', '==', false)
      .where('ownerId', '==', userId);

    // Utility Builders
    const queryBuilder = (validator: boolean, queryStatement: any[]) => (baseQuery: any) => {
      if (validator) {
        return baseQuery.where(...queryStatement);
      }

      return baseQuery;
    };

    const orderQueryBuilder = (validator: boolean) => (baseQuery: any) => {
      return validator ? baseQuery.orderBy('name', 'desc') : baseQuery.orderBy('name');
    };

    const paginationBuilder = (lastItem: object | null) =>
      (baseQuery: any) => config.sort === 'name-descend' && !config.pagination.lastItem
        ? baseQuery.endBefore(lastItem)
        : baseQuery.startAfter(lastItem);

    // Builder Instantiations
    const onlyQueryPublic = queryBuilder(
      config.filter === 'public',
      ['type', '==', 'Public'],
    );

    const onlyQueryClosed = queryBuilder(
      config.filter === 'closed',
      ['type', '==', 'Closed'],
    );

    const searchTermQuery = (baseQuery: any) => {
      return config.search
        ? baseQuery.where('searchTerms', 'array-contains', config.search)
        : baseQuery;
    };

    const orderedQuery = orderQueryBuilder(config.sort === 'name-descend');

    const paginate = paginationBuilder(config.pagination.lastItem);

    // Built Query
    const buildQuery = (query: any) => [query]
      .map(onlyQueryPublic)
      .map(onlyQueryClosed)
      .map(searchTermQuery)
      .map(orderedQuery)
      .map(paginate)
      .reduce((acc, value) => {
        return value;
      }, {});

    // Called Query
    const documents = await buildQuery(documentQuery)
      .limit(config.pagination.limit)
      .get();

    // Format Query Results to a new Array
    const docsArray: any[] = [];
    documents.forEach((data: any) => {
      const docsData = {
        id: '',
        name: '',
        type: '',
        members: [],
      };

      docsData.id = data.id;
      docsData.name = data.data().name;
      docsData.type = data.data().type;

      docsArray.push(docsData);
    });

    return {
      groups: docsArray,
      lastItem: documents.docs[documents.docs.length - 1],
    };
  };
export const getGroupsByLeaderId = async (leaderId: string) => {
  const document = await groupsCollection
      .where('ownerId', '==', leaderId)
      .where('archived', '==', false)
      .get();
  const groupsArray: any[] = [];
  document.forEach((data:any) => {
    const dataToPush = {
      id: '',
      name: '',
      type: '',
      archived: '',
    };
    dataToPush.id = data.id;
    dataToPush.name = data.data()?.name;
    dataToPush.type = data.data().type;
    dataToPush.archived = data.data()?.archived;
    groupsArray.push(dataToPush);
  });
  return groupsArray;
};
export const getGroupById =
  async (groupId: string): Promise<Object> => {
    if (!groupId) {
      throw new Error('No Group Id was provided');
    }
    const document =
      await groupsCollection
        .doc(groupId)
        .get();

    return {
      id: document.id,
      name: document.data()?.name,
      ownerId: document.data()?.ownerId,
      type: document.data()?.type,
      archived: document.data()?.archived,
    };
  };

export const getGroupMembers =
  async (groupId: string): Promise<Object> => {
    if (!groupId) {
      throw new Error('No Group Id was provided');
    }

    const documents =
      await groupsCollection
        .doc(groupId)
        .collection('members')
        .get();

    const arrayMembers: any[] = [];
    documents.forEach((doc) => {
      arrayMembers.push({
        id: doc.data().id,
        name: doc.data().name,
        email: doc.data().email,
        // date: moment(data.data().date.seconds * 1000).format('MM/DD/YYYY'),
        joined: moment(doc.data().joinedDate.seconds * 1000).format('MM/DD/YYYY'),
        approved: moment(doc.data().approvedDate * 1000).format('MM/DD/YYYY'),
        status: doc.data().status,
        type: doc.data().type,
      });
    });

    return Promise.resolve({
      groupId,
      members: arrayMembers,
    });

  };

export const addNewGroupMember =
  async (groupId: string, member: any): Promise<void> => {
    return await groupsCollection
      .doc(groupId)
      .collection('members')
      .doc(member.email)
      .set({
        type: member.type,
        id: member.uid,
        name: member.displayName,
        email: member.email,
        status: member.status,
        approvedDate: firebase.firestore.FieldValue.serverTimestamp(),
        joinedDate: firebase.firestore.FieldValue.serverTimestamp(),
        created: firebase.firestore.FieldValue.serverTimestamp(),
        updated: firebase.firestore.FieldValue.serverTimestamp(),
      });
  };

export const updateGroupMember =
  async (groupId: string, email: string, memeberUpdates: object): Promise<void> => {
    return await groupsCollection
      .doc(groupId)
      .collection('members')
      .doc(email)
      .update({
        ...memeberUpdates,
        updated: firebase.firestore.FieldValue.serverTimestamp(),
      });
  };

export const approveGroupMember =
  async (groupId: string, member: any): Promise<void> => {
    return await groupsCollection
      .doc(groupId)
      .collection('members')
      .doc(member.email)
      .update({
        approvedDate: firebase.firestore.FieldValue.serverTimestamp(),
        status: 'approved',
        updated: firebase.firestore.FieldValue.serverTimestamp(),
      });
  };

export const removeGroupMember =
  async (groupId: string, member: any): Promise<void> => {
    return await groupsCollection
      .doc(groupId)
      .collection('members')
      .doc(member.email)
      .delete();
  };

export const userCanAccess =
  async (groupId: string, member: any): Promise<boolean> => {
    const groupMember = await groupsCollection
      .doc(groupId)
      .collection('members')
      .doc(member.email)
      .get();

    return groupMember.data()?.status === 'approved';
  };

export const deleteGroupsForUser = async (userId: string): Promise<any> => {
  return await groupsCollection.where('ownerId', '==', userId).get()
      .then((groupQuerySnapshot) => {
        groupQuerySnapshot.forEach((groupDocument) => {
          const groupId = groupDocument.id;
          groupsCollection
            .doc(groupId)
            .collection('members')
            .get()
            .then((membersQuerySnapshot) => {
              membersQuerySnapshot.forEach(async (memberDocument: { data: () => any; })  => {
                const memberData = memberDocument.data();
                const memberId = memberData.id;
                const followingEntry = usersCollection
                    .doc(memberId)
                    .collection('following')
                    .doc(userId);
                await followingEntry.delete();
              });
            });
          groupsCollection
              .doc(groupDocument.id)
              .collection('members')
              .doc(groupDocument.id)
              .delete();
          groupsCollection.doc(groupDocument.id).delete();
        });
      });
};
